import { EMAIL_REGEXP } from '@/constants';
import translate from '@/utils/translate';
import { EmploymentStatus } from '@/hooks/webapi/types/EmploymentStatus';
import { areEmployerDetailsRequired } from './EmployerClient';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidationFunc = (value: any) => string | undefined;

// Runs all validators and concatenates their error messages with '-divider-' separator for multi-error display
export function applyAll(...validations: Array<ValidationFunc>) {
  return (value: unknown) => {
    const errors = validations.reduce((acc, cur) => {
      const err = cur(value);
      if (err) {
        return `${err}-divider-${acc}`;
      }
      return acc;
    }, '');

    return errors.length > 0 ? errors : undefined;
  };
}

export const passwordSameAsOld = (source: string) => (target: string) => {
  if (source === target) {
    return translate('error.validation.password_is_same_as_old');
  }
  return undefined;
};

export const isPrimativeEmpty = (value: unknown): boolean => {
  // excluding zero
  return value === 0 ? false : !value;
};

// NOTE: Later on we can add a check for min/max quantity of letters and numbers
// À-ž range covers Latin Extended characters including Czech diacritics alongside standard ASCII letters
export const hasAlphanumericChars = (subject?: string) => (value?: string) => {
  const IS_CHAR = /[À-ža-zA-Z]/g;
  const IS_NUMERIC = /[0-9]/g;

  if (
    value &&
    value.match(IS_CHAR)?.length &&
    value.match(IS_NUMERIC)?.length
  ) {
    return undefined;
  }

  return subject
    ? translate('error.validation.has_alphanumeric_chars_with_subject', {
        subject,
      })
    : translate('error.validation.has_alphanumeric_chars');
};

export const hasSpaces = (value?: string | null): string | undefined => {
  // Password must not contain any kind of whitespaces
  const HAS_SPACES_REGEX = /\s/;

  if (value && HAS_SPACES_REGEX.test(value)) {
    return translate('error.validation.password_contains_space');
  }
  return undefined;
};

export const empty = (value?: unknown) => {
  if (isPrimativeEmpty(value)) {
    return translate('error.validation.empty');
  }
  return undefined;
};

// Only enforces the required check when the employment type demands employer details (EMPLOYEE or SELF_EMPLOYED)
export const employerValidation =
  (employmentStatus: EmploymentStatus | undefined) =>
  (value: string | undefined) => {
    if (areEmployerDetailsRequired(employmentStatus)) return empty(value);
    return undefined;
  };

export const notEmail = (value?: string | null): string | undefined => {
  if (!EMAIL_REGEXP.test(String(value))) {
    return translate('error.validation.not_email');
  }
  return undefined;
};

export const equal = (value1?: string | null, value2?: string | null) => {
  if (value1 !== value2) {
    return translate('error.validation.not_equal');
  }
  return undefined;
};

export const notMobilePhone = (value?: string | null): string | undefined => {
  // Phone must start with 6 or 7, only numbers, 9 digits length, optionally with country code
  const MOBILE_PHONE_REGEX = /^(\+?420)?[67]([0-9]{8})$/;

  if (!MOBILE_PHONE_REGEX.test(String(value))) {
    return translate('error.validation.invalid_phone_number');
  }
  return undefined;
};

export const minLength =
  (length: number, subject?: string) => (value?: string | null) => {
    if (typeof value === 'undefined' || String(value).trim().length < length) {
      return subject
        ? translate('error.validation.min_length_with_subject', {
            length,
            subject,
          })
        : translate('error.validation.min_length', { length });
    }
    return undefined;
  };

export const maxLength =
  (length: number, subject?: string) => (value?: string | null) => {
    if (typeof value === 'undefined' || String(value).trim().length > length) {
      return subject
        ? translate('error.validation.max_length_with_subject', {
            length,
            subject,
          })
        : translate('error.validation.max_length', { length });
    }
    return undefined;
  };

/** blocks completely the use of diacritics characters, used for password validation */
export const restrictDiacriticsChars = (
  value?: string | null,
): string | undefined => {
  const DIACRITICS_REGEX = /[À-ž]/;

  if (value && DIACRITICS_REGEX.test(value)) {
    return translate('error.validation.restrict_diacritics_chars');
  }
  return undefined;
};

export const restrictSpecialChars = (value?: string) => {
  const SPECIAL_CHAR_REGEX = /[^À-ža-zA-Z0-9-_\s]/;

  if (value && SPECIAL_CHAR_REGEX.test(value)) {
    return translate('error.validation.restrict_special_chars');
  }
  return undefined;
};

export const restrictFileSize =
  (maxSize: number, placeholder: string) => (file: File) => {
    if (file && file.size > maxSize) {
      return translate('error.validation.file_size_too_big', {
        placeholder,
      });
    }
    return undefined;
  };

export const invalidFileFormatType = (validTypes: string[]) => (file: File) => {
  if (file && !validTypes.includes(file.type)) {
    return translate('error.validation.file_format_invalid');
  }
  return undefined;
};

export const rangeValue =
  (minValue: number, maxValue: number, validText?: string) =>
  (value?: string | null) => {
    if (Number(value) > maxValue || Number(value) < minValue) {
      return validText || translate('error.validation.out_of_range');
    }
    return undefined;
  };

export const firstNumLarger =
  (firstValue: number, secondValue: number, validText?: string) => () => {
    if (firstValue > secondValue) {
      return validText;
    }
    return undefined;
  };
